// src/app/childMode.ts
//
// Child mode: a parent hands the device to a young child (ages 6–9 learn on
// the parent's account). The portal then shows only that child's lessons,
// in a bigger, simpler layout, and a grown-up gate is needed to leave.
//
// It's remembered on this device only (localStorage) and doesn't change the
// sign-in: it's a screen lock for small hands, not a security boundary.

import { useEffect, useState } from "react";

const KEY = "fsl-child-mode";
const EVENT = "fsl-child-mode-change";

export interface ChildModeState {
  childId: string;
  firstName: string;
}

export function getChildMode(): ChildModeState | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ChildModeState) : null;
  } catch {
    return null;
  }
}

export function startChildMode(state: ChildModeState) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable: child mode can't be kept */
  }
  window.dispatchEvent(new Event(EVENT));
}

export function endChildMode() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(EVENT));
}

/** The current child mode (or null), updated when it starts or ends. */
export function useChildMode(): ChildModeState | null {
  const [state, setState] = useState<ChildModeState | null>(getChildMode);
  useEffect(() => {
    const update = () => setState(getChildMode());
    window.addEventListener(EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);
  return state;
}

/** A sum a grown-up can do quickly and a 6–9-year-old usually can't. */
export function grownUpQuestion() {
  const a = 12 + Math.floor(Math.random() * 8); // 12–19
  const b = 6 + Math.floor(Math.random() * 4); // 6–9
  return { text: `${a} × ${b}`, answer: a * b };
}
